'use client';

import { useState, useEffect, useMemo } from 'react';

interface SectionItem {
  id: string;
  label: string;
}

interface SectionAnchorNavProps {
  scrollOffset?: number;
}

const SectionAnchorNav = ({ scrollOffset = 60 }: SectionAnchorNavProps) => {
  const [activeSection, setActiveSection] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  const sections: SectionItem[] = useMemo(
    () => [
      { id: 'hero', label: 'Hero' },
      { id: 'process', label: 'Process' },
      { id: 'case-studies', label: 'Launches' },
      { id: 'faq', label: 'FAQs' },
      { id: 'start-mvp', label: 'Start' },
    ],
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);

      const currentSection = sections
        .map((s) => s.id)
        .find((sectionId) => {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= scrollOffset + 150 && rect.bottom >= scrollOffset + 150;
          }
          return false;
        });

      if (currentSection) setActiveSection(currentSection);
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const throttledScroll = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        handleScroll();
        timeoutId = null;
      }, 16);
    };

    window.addEventListener('scroll', throttledScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [scrollOffset, sections]);

  const handleSectionClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({ top: element.offsetTop - scrollOffset, behavior: 'smooth' });
    }
  };

  if (!isVisible) return null;

  return (
    <nav
      className="fixed right-5 top-1/2 z-[900] hidden -translate-y-1/2 lg:block"
      aria-label="Section navigation"
    >
      <div className="flex flex-col gap-3">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className="group relative flex items-center"
            aria-label={`Navigate to ${section.label} section`}
            aria-current={activeSection === section.id ? 'true' : 'false'}
          >
            <div
              className={`h-1.5 w-1.5 rounded-full transition-colors duration-250 ${
                activeSection === section.id
                  ? 'bg-primary'
                  : 'bg-border group-hover:bg-text-secondary'
              }`}
            />
            <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap text-xs font-medium text-text-secondary opacity-0 transition-opacity group-hover:opacity-100">
              {section.label}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export default SectionAnchorNav;
